"use client";

import Image from "next/image";
import { Loader2, Lock, Play } from "lucide-react";
import { useDict } from "@/components/i18n/DictProvider";
import { cn } from "@/components/ui/cn";
import { formatDuration, formatEpisodes } from "@/lib/format";
import { type Lang } from "@/lib/i18n";
import { OttRailCard } from "./OttRailCard";
import { episodeThumb } from "./ottMeta";
import { episodeState } from "./ottEpisodes";
import type { OttVideo } from "@/lib/lecturehub/types";

/**
 * Series episode list for the OTT watch rail — the playlist beside the stage
 * (N01, decision D32). Each row is a button that puts its episode on stage;
 * the playing row is marked. A row's state is the episode's OWN
 * (`episodeState`): a lock on the thumb for an episode this caller may not
 * stream, a spinner for one still transcoding — a locked series can still
 * carry playable episodes, and the rows say which.
 *
 * Short series only (fewer than EPISODE_STRIP_MIN episodes): longer runs render
 * as OttEpisodeStrip under the player instead. Posters resolve through
 * episodeThumb; an unrenderable src falls back to the branded play tile.
 */
export function OttEpisodeList({
  videos,
  lang,
  cfHash = null,
  selectedIndex = -1,
  onSelect,
}: {
  videos: OttVideo[];
  lang: Lang;
  /** Portal.cloudflareAccountHash — CF-optimized episode thumbs when present. */
  cfHash?: string | null;
  /** The episode on stage. */
  selectedIndex?: number;
  onSelect?: (index: number) => void;
}) {
  const t = useDict();
  const total = videos.reduce((sum, v) => sum + (v.duration ?? v.video?.duration ?? 0), 0);
  // "4 epizode · 1h 25m" — a real count phrase, not a bare digit; and never
  // uppercased, which turned the runtime into "1H 25M".
  const count = formatEpisodes(videos.length, lang, t.units);
  const meta = total > 0 ? `${count} · ${formatDuration(total)}` : count;

  return (
    <OttRailCard title={t.detail.episodes} meta={meta}>
      <ol className="max-h-[60vh] divide-y divide-border/50 overflow-y-auto px-2 py-1">
        {videos.map((v, i) => {
          const duration = v.duration ?? v.video?.duration ?? 0;
          const src = episodeThumb(v, cfHash);
          const state = episodeState(v);
          const active = i === selectedIndex;
          const stateLabel =
            state === "locked"
              ? t.detail.episodeLocked
              : state === "processing"
                ? t.detail.episodeProcessing
                : state === "unavailable"
                  ? t.detail.episodeUnavailable
                  : null;
          return (
            <li key={v.id}>
              <button
                type="button"
                onClick={onSelect ? () => onSelect(i) : undefined}
                aria-current={active ? "true" : undefined}
                className={cn(
                  "grid w-full grid-cols-[22px_88px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-muted",
                  active && "bg-muted",
                )}
              >
                <span
                  className={cn(
                    "text-right font-display text-[11px] font-extrabold tabular-nums",
                    active ? "text-primary" : "text-foreground/35",
                  )}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                {/* Hairline edge via ::after so it sits ABOVE the fill image — an
                    inset ring on the span itself would paint underneath it. */}
                <span
                  className={cn(
                    "relative block aspect-video w-full overflow-hidden rounded-md bg-gradient-to-br from-teal to-foreground after:pointer-events-none after:absolute after:inset-0 after:rounded-md after:ring-inset after:content-['']",
                    active ? "after:ring-2 after:ring-primary" : "after:ring-1 after:ring-black/10",
                  )}
                >
                  {src ? (
                    // alt="" — the episode title is the next cell in this row.
                    <Image src={src} alt="" fill sizes="88px" className="object-cover" />
                  ) : (
                    // No episode poster → a branded tile with a play glyph, not the
                    // generic "No image" box (episodes rarely carry their own art).
                    <span
                      aria-hidden
                      className="absolute inset-0 grid place-items-center text-white/70"
                    >
                      <Play className="size-4 fill-current" />
                    </span>
                  )}
                  {state === "locked" || state === "processing" ? (
                    <span
                      aria-hidden
                      className="absolute inset-0 grid place-items-center bg-black/45 text-white"
                    >
                      {state === "locked" ? (
                        <Lock className="size-3.5" />
                      ) : (
                        <Loader2 className="size-3.5 animate-spin" />
                      )}
                    </span>
                  ) : null}
                </span>
                <span className="min-w-0">
                  <span
                    className={cn(
                      "line-clamp-2 block font-display text-[13px] font-bold leading-[1.3]",
                      active ? "text-primary" : "text-foreground",
                    )}
                  >
                    {v.title}
                  </span>
                  {stateLabel ? (
                    <span className="mt-0.5 block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      {stateLabel}
                    </span>
                  ) : null}
                </span>
                {duration > 0 ? (
                  <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
                    {formatDuration(duration)}
                  </span>
                ) : (
                  <span />
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </OttRailCard>
  );
}
